import React, { useState } from 'react';
import SEO from '../components/SEO';
import { FaChevronDown, FaChevronUp } from 'react-icons/fa';

interface FAQItem {
  question: string;
  answer: string;
}

const FAQ: React.FC = () => {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  const faqs: FAQItem[] = [
    {
      question: 'What is a casino welcome bonus?',
      answer: `A welcome bonus is a promotional offer given to new players when they sign up
      and make their first deposit at an online casino. These bonuses typically match a
      percentage of your deposit (e.g., 100% up to £100), giving you extra funds to play with.`,
    },
    {
      question: 'What are wagering requirements?',
      answer: `Wagering requirements (also called playthrough requirements) specify how many
      times you must bet the bonus amount before you can withdraw any winnings. For example,
      if you receive a £100 bonus with 35x wagering, you must bet £3,500 before withdrawing.`,
    },
    {
      question: 'How do I claim a casino bonus?',
      answer: `To claim a casino bonus, you typically need to: 1) Create an account at the casino,
      2) Make a qualifying deposit (usually a minimum amount), 3) Enter any bonus code if required,
      4) The bonus will be credited to your account automatically or after contacting support.`,
    },
    {
      question: 'Are casino bonuses worth it?',
      answer: `Casino bonuses can be valuable if you understand the terms. Look for bonuses with
      lower wagering requirements (30x or less), reasonable maximum bet limits, and games that
      contribute fully to wagering. Always read the terms and conditions before claiming.`,
    },
    {
      question: 'How long do I have to use a bonus?',
      answer: `Most casino bonuses have an expiration date, typically ranging from 7 to 30 days
      from when they're credited. Some bonuses must be used immediately. Always check the terms
      to avoid losing your bonus.`,
    },
    {
      question: 'Can I withdraw bonus funds immediately?',
      answer: `No, you cannot withdraw bonus funds immediately. You must meet the wagering
      requirements first. Once you've completed the required wagering, any remaining bonus
      funds and winnings can be withdrawn according to the casino's withdrawal policy.`,
    },
    {
      question: 'What games count towards wagering?',
      answer: `Different games contribute different percentages to wagering requirements. Slots
      usually contribute 100%, while table games like blackjack and roulette often contribute
      only 10-20%. Some games may be excluded entirely. Always check the bonus terms.`,
    },
    {
      question: 'Is it safe to play at UK online casinos?',
      answer: `Yes, if you play at casinos licensed by the UK Gambling Commission. These casinos
      must meet strict standards for player protection, fair gaming, and responsible gambling.
      Always verify a casino's license before signing up.`,
    },
    {
      question: 'How do I know if a casino is trustworthy?',
      answer: `Look for: UK Gambling Commission license, positive player reviews, fair bonus terms,
      fast withdrawal times, responsive customer support, and secure payment methods. We only
      feature casinos that meet these standards.`,
    },
    {
      question: 'What should I do if I have a gambling problem?',
      answer: `If you feel you may have a gambling problem, seek help immediately. Visit
      BeGambleAware.org for free support and resources. You can also use self-exclusion tools
      at casinos or contact the National Gambling Helpline at 0808 8020 133.`,
    },
  ];

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };

  return (
    <>
      <SEO
        title="Frequently Asked Questions"
        description="Find answers to common questions about UK casino bonuses, wagering requirements, withdrawals, and more."
        url="/faq"
        schema={schema}
      />

      <div className="pt-24 pb-20 min-h-screen bg-dark-bg">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <h1 className="text-4xl md:text-5xl font-bold mb-4 text-center">
              Frequently Asked Questions
            </h1>
            <p className="text-gray-400 text-center mb-12">
              Everything you need to know about casino bonuses and offers
            </p>

            <div className="space-y-4">
              {faqs.map((faq, index) => (
                <div
                  key={index}
                  className="bg-dark-card border border-dark-border rounded-xl overflow-hidden"
                >
                  <button
                    onClick={() => setOpenIndex(openIndex === index ? null : index)}
                    className="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-dark-surface transition-colors"
                  >
                    <span className="text-white font-semibold pr-4">{faq.question}</span>
                    {openIndex === index ? (
                      <FaChevronUp className="text-yellow-400 flex-shrink-0" />
                    ) : (
                      <FaChevronDown className="text-gray-400 flex-shrink-0" />
                    )}
                  </button>
                  {openIndex === index && (
                    <div className="px-6 pb-4">
                      <p className="text-gray-300 leading-relaxed">{faq.answer}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>

            <div className="mt-12 bg-dark-card border border-dark-border rounded-xl p-8 text-center">
              <h2 className="text-2xl font-bold text-white mb-4">Still Have Questions?</h2>
              <p className="text-gray-300 mb-6">
                Can't find the answer you're looking for? Contact us and we'll be happy to help.
              </p>
              <a
                href="mailto:support@turbobetto.com"
                className="inline-block px-6 py-3 bg-gradient-to-r from-yellow-400 to-orange-500 hover:from-yellow-500 hover:to-orange-600 text-dark-bg rounded-lg font-semibold transition-all"
              >
                Contact Support
              </a>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default FAQ;

